<template>
  <div class="chat-message-list flex-1 min-h-0 flex flex-col">
    <!-- React query-ai chat host. We mount the existing AIChat component
         (the same one chat.vue and AtlasAIDrawer used) so message rendering,
         streaming, and session persistence are unchanged. The only thing we
         vary per-route is `connectionId`, which scopes sessions in the
         backend chat-session API. -->
    <div
      ref="hostEl"
      class="flex-1 min-h-0 overflow-hidden"
    />
  </div>
</template>

<script lang="ts">
/**
 * ChatMessageList
 *
 * Wraps the existing React query-ai chat (src/query-ai/components/ai-chat.tsx)
 * inside a Vue component so the AI panel can reuse the proven streaming +
 * session backend without re-implementing transport.
 *
 * Reused from existing surfaces:
 *   - mountAIChat / unmountAIChat / updateTheme  (../../query-ai/mount.tsx)
 *   - apiUrl '/platform-data/chat/atlas'         (matches chat.vue + AtlasAIDrawer)
 *   - session list endpoint
 *     /platform-data/query-ai/chat/sessions/search
 *     (used by chat-session-list.tsx — connectionId scoped)
 *
 * The React component listens for the `query-ai:open-chat-with-question`
 * window event and seeds it as a question, so AiChatPanelStore.send() works
 * by dispatching that event.
 */
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onUnmounted,
  inject,
  computed,
  nextTick
} from 'vue'
import { useTheme } from '../../utils/useTheme'

const ATLAS_API_URL = '/platform-data/chat/atlas'

export default defineComponent({
  name: 'ChatMessageList',
  props: {
    /**
     * Per-route connectionId. Changing this remounts the React chat and loads
     * the sessions for the new bucket — that's how per-route threads work.
     */
    connectionId: { type: String, required: true },
    /** Optional: auto-run this question after mount (e.g. quick-action click). */
    autoRunQuestion: { type: String, default: '' }
  },
  setup(props) {
    const theme = inject('theme', useTheme())
    const isDark = computed(() => theme.isDark.value)
    const hostEl = ref<HTMLElement | null>(null)
    let mounted = false
    let mountedConnectionId = ''

    async function mountReact(autoRun?: string) {
      if (!hostEl.value || mounted) return
      const { mountAIChat } = await import('../../query-ai/mount')
      mountAIChat(hostEl.value, {
        theme: isDark.value ? 'dark' : 'light',
        apiUrl: ATLAS_API_URL,
        connectionId: props.connectionId,
        scope: 'atlas',
        ...(autoRun ? { autoRunQuestion: autoRun } : {})
      })
      mounted = true
      mountedConnectionId = props.connectionId
    }

    async function unmountReact() {
      if (!mounted) return
      const el = hostEl.value
      mounted = false
      mountedConnectionId = ''
      if (!el) return
      const { unmountAIChat } = await import('../../query-ai/mount')
      unmountAIChat(el)
    }

    onMounted(async () => {
      await nextTick()
      await mountReact(props.autoRunQuestion || undefined)
    })

    onUnmounted(unmountReact)

    // Remount when the connectionId changes (route switch → new thread bucket).
    watch(
      () => props.connectionId,
      async (next) => {
        if (!next || next === mountedConnectionId) return
        await unmountReact()
        await nextTick()
        await mountReact()
      }
    )

    // Keep React's theme in sync with Vue's theme toggle.
    watch(
      () => isDark.value,
      async (dark) => {
        if (!hostEl.value || !mounted) return
        const { updateTheme } = await import('../../query-ai/mount')
        updateTheme(hostEl.value, dark ? 'dark' : 'light')
      }
    )

    return { hostEl }
  }
})
</script>

<style scoped>
.chat-message-list {
  min-height: 0;
}
</style>
